import React from 'react';
import SEO from '../../components/SEO';
import { motion } from 'framer-motion';
import PageBanner from '../../components/PageBanner';

const ConceptCarPage = () => {
    const fadeIn = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.8 }
    };

    return (
        <div className="bg-white min-h-screen pt-20 lg:pt-[120px] overflow-hidden">
            <SEO
                title="BAIC Concept Cars | Future of Mobility Design"
                description="Experience the future with BAIC's Concept Cars. Featuring 'Joy' design philosophy, combining technology and human-centric design."
                canonicalUrl="https://baicoman.com/about/concept-car"
            />
            <h1 className="sr-only">BAIC Concept Cars - Future Design Innovation</h1>
            {/* Hero Section */}
            <PageBanner
                image="/assets/images/concept-car/734_1718970878banner-concept-car.png"
                imageAlt="Concept Car Hero"
                contentClassName="items-center text-center"
            >
                <motion.h2
                    className="text-3xl md:text-8xl font-black text-white uppercase tracking-widest text-center"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                >
                    Concept<br />Car
                </motion.h2>
            </PageBanner>

            {/* Joy Design Concept */}
            <section className="py-24 max-w-7xl mx-auto px-4">
                <div className="flex flex-col md:flex-row justify-between items-start gap-12">
                    <motion.div
                        className="md:w-1/2"
                        {...fadeIn}
                    >
                        <h2 className="text-3xl font-bold uppercase mb-6 text-gray-900 leading-tight">
                            JOY: Keywords of the Design<br />Concept Are...
                        </h2>
                        <p className="text-gray-600 leading-relaxed text-justify">
                            The core design philosophy revolves around "Joy," capturing the essence of
                            driving pleasure and emotional connection. It represents a bold leap
                            into the future, where technology meets human-centric design to create
                            unforgettable journeys.
                        </p>
                    </motion.div>
                    <motion.div
                        className="md:w-1/3 flex justify-end"
                        {...fadeIn}
                        transition={{ delay: 0.2 }}
                    >
                        <img src="/assets/images/concept-car/516_1718717795joy.png" alt="Joy Calligraphy" className="w-48 md:w-64" />
                    </motion.div>
                </div>
            </section>

            {/* Car Evolution / Variations */}
            <section className="bg-black py-20 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    {/* Using the variations banner as a base background or graphic */}
                    <div className="relative mb-16">
                        <img src="/assets/images/concept-car/784_1718791002banner-car-variations.png" alt="Car Variations" className="w-full max-w-4xl mx-auto opacity-70" />

                    </div>
                </div>
            </section>

            {/* Aerodynamics / Tech Section (from mockup middle part with car lines) */}
            <section className="py-0">
                <div className="relative h-[400px] md:h-[600px] bg-gray-900 group overflow-hidden">
                    {/* Using car_image1 or 2 which look like the side profiles */}
                    <img src="/assets/images/concept-car/car_image1.BC6EdTbW.jpg" alt="Aerodynamics" className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 flex items-center justify-center">
                        {/* Arrows overlay */}
                        <div className="relative w-full max-w-5xl px-4 flex justify-between">
                            <img src="/assets/images/concept-car/car_image1_arrow_left.D1yCG5M1.png" alt="Arrow Left" className="w-24 md:w-48 animate-pulse" />
                            <img src="/assets/images/concept-car/car_image1_arrow_right.BDynP03-.png" alt="Arrow Right" className="w-24 md:w-48 animate-pulse" />
                        </div>
                    </div>
                </div>
                <div className="relative h-[400px] md:h-[600px] bg-gray-900 group overflow-hidden mt-4">
                    <img src="/assets/images/concept-car/car_image2.C1Sf7hHa.jpg" alt="Performance" className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <img src="/assets/images/concept-car/car_line1.DIaJztQ_.png" alt="Flow Lines" className="w-full max-w-6xl opacity-80" />
                    </div>
                </div>
            </section>

            {/* Oriental & Stylish */}
            <section className="py-24 max-w-7xl mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <motion.div
                        className="space-y-6"
                        {...fadeIn}
                    >
                        <h2 className="text-5xl font-bold text-gray-900 mb-8">Oriental &<br /><span className="text-gray-400">Stylish</span></h2>
                        <p className="text-gray-600 pl-4 border-l-4 border-red-600">
                            A fusion of traditional aesthetics and modern luxury.
                            The interior design draws inspiration from oriental architecture,
                            creating a serene and sophisticated cabin atmosphere.
                        </p>
                        <img src="/assets/images/concept-car/636_1718887237oriental_stylish1.png" alt="Interior Detail" className="w-full rounded-2xl shadow-xl mt-8" />
                    </motion.div>
                    <motion.div
                        {...fadeIn}
                        transition={{ delay: 0.2 }}
                    >
                        <img src="/assets/images/concept-car/437_1718887237oriental_stylish2.png" alt="Interior View" className="w-full rounded-2xl shadow-2xl" />
                    </motion.div>
                </div>
            </section>

            {/* Scenario-based Design */}
            <section className="py-12">
                <div className="relative">
                    <img src="/assets/images/concept-car/321_1719492045scenario-based-design.png" alt="Scenario Based Design" className="w-full object-cover" />
                </div>
            </section>

            {/* Details Grid */}
            <section className="py-24 max-w-7xl mx-auto px-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
                    {[
                        { img: '119_1718970880panel1.png', label: 'Console' },
                        { img: '496_1718970880panel2.png', label: 'Details' },
                        { img: '54_1718970880panel3.png', label: 'Texture' },
                        { img: '152_1718970880panel4.png', label: 'Light' }
                    ].map((item, idx) => (
                        <motion.div
                            key={idx}
                            className="group relative overflow-hidden rounded-xl aspect-square"
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ delay: idx * 0.1 }}
                        >
                            <img src={`/assets/images/concept-car/${item.img}`} alt={item.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/0 transition-colors" />
                        </motion.div>
                    ))}
                </div>
            </section>

        </div>
    );
};

export default ConceptCarPage;
